/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — palette bar (drag source for adding members/teams
   onto the chart). Placement rules ABSOLUTE (People Rebuild §3):
   pool = active members NOT on the chart; no hidden-state affordance
   exists anywhere in this surface. Behavior ported from the old
   palette section of OrgChart.tsx.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { X } from 'lucide-react';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import type { TeamMember, Team } from '../../../../shared/types';
import { MemberToken, TeamToken } from './nodes';

export interface PaletteBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  offChartMembers: TeamMember[];
  teams: Team[];
  getMemberTeamColor: (memberId: string) => string;
}

export function PaletteBar({
  search,
  onSearchChange,
  offChartMembers,
  teams,
  getMemberTeamColor,
}: PaletteBarProps) {
  const sq = search.toLowerCase();
  const fm = sq
    ? offChartMembers.filter(
        (m) => m.name.toLowerCase().includes(sq) || (m.email || '').toLowerCase().includes(sq)
      )
    : offChartMembers;
  const ft = sq
    ? teams.filter(
        (t) => t.name.toLowerCase().includes(sq) || (t.shortCode || '').toLowerCase().includes(sq)
      )
    : teams;

  return (
    <div
      style={{
        background: 'var(--sa)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--bd)',
        padding: rem(8) + ' ' + rem(10),
        display: 'flex',
        gap: rem(10),
        marginBottom: 'var(--space-2)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <div style={{ position: 'relative' }}>
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search people or teams…"
            aria-label="Search people or teams to add to the chart"
            style={{
              width: rem(140),
              padding: rem(5) + ' var(--space-2)',
              paddingRight: search ? rem(22) : 'var(--space-2)',
              borderRadius: rem(5),
              border: '1px solid var(--bd)',
              background: 'var(--ib)',
              color: 'var(--tx)',
              fontSize: 'var(--font-size-base)',
            }}
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              title="Clear search"
              style={{
                position: 'absolute',
                right: 'var(--space-1)',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--tm)',
                cursor: 'pointer',
                lineHeight: 1,
                padding: 'var(--space-1)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Icon icon={X} size="md" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <div
        style={{ width: rem(1), alignSelf: 'stretch', background: 'var(--bd)', flexShrink: 0 }}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-1)',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: rem(6),
            alignItems: 'center',
            overflow: 'auto',
            padding: rem(2) + ' 0',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: rem(0.5),
              flexShrink: 0,
            }}
          >
            People
          </span>
          <div style={{ width: rem(1), height: rem(20), background: 'var(--bd)', flexShrink: 0 }} />
          {fm.length > 0 ? (
            fm.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: rem(2),
                  flexShrink: 0,
                }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(
                    'application/json',
                    JSON.stringify({ type: 'member', id: m.id })
                  );
                  e.dataTransfer.effectAllowed = 'move';
                }}
              >
                <MemberToken
                  member={m}
                  color={getMemberTeamColor(m.id)}
                  // eslint-disable-next-line sf/no-raw-px -- design-surface avatar size prop (nodes.tsx), not a lucide icon
                  size={32}
                  style={{ cursor: 'grab' }}
                />
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--tm)',
                    maxWidth: rem(48),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}
                >
                  {m.name.split(' ')[0]}
                </span>
              </div>
            ))
          ) : (
            <span
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', fontStyle: 'italic' }}
            >
              No matches
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            gap: rem(6),
            alignItems: 'center',
            overflow: 'auto',
            padding: rem(2) + ' 0',
            borderTop: '1px solid var(--bd)',
            paddingTop: 'var(--space-1)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: rem(0.5),
              flexShrink: 0,
            }}
          >
            Teams
          </span>
          <div style={{ width: rem(1), height: rem(20), background: 'var(--bd)', flexShrink: 0 }} />
          {ft.length > 0 ? (
            ft.map((t) => (
              <TeamToken
                key={t.id}
                team={t}
                // eslint-disable-next-line sf/no-raw-px -- design-surface avatar size prop (nodes.tsx), not a lucide icon
                size={28}
                style={{ cursor: 'grab' }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(
                    'application/json',
                    JSON.stringify({ type: 'team', id: t.id })
                  );
                  e.dataTransfer.effectAllowed = 'move';
                }}
              />
            ))
          ) : (
            <span
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', fontStyle: 'italic' }}
            >
              {teams.length === 0 ? 'No teams yet' : 'No matches'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
